import { useState, useCallback } from 'react';
import { patientService } from '../services/patientService';

export interface PatientListItem {
  id: number;
  medical_record_number: string;
  first_name: string;
  last_name: string;
  identification: string;
  birth_date: string;
  gender: boolean | null;
  status: number;
}

export const usePatientList = () => {
  const [patients, setPatients] = useState<PatientListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 15,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const fetchPatients = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: pagination.perPage,
        ...(search && { search }),
      };

      const response = await patientService.getAll(params);

      setPatients(response.data || []);
      setPagination(prev => ({
        ...prev,
        currentPage: response.current_page || page,
        totalPages: response.last_page || 1,
        total: response.total || 0,
      }));
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError('Error loading patients');
    } finally {
      setLoading(false);
    }
  }, [pagination.perPage]);

  const searchPatients = useCallback(async (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      await fetchPatients(1, '');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await patientService.search(term);
      setPatients(results || []);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalPages: 1,
        total: results?.length || 0,
      }));
    } catch (err) {
      console.error('Error searching patients:', err);
      setError('Error searching patients');
    } finally {
      setLoading(false);
    }
  }, [fetchPatients]);

  const deletePatient = useCallback(async (id: number) => {
    setPendingDeleteId(id);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteId == null) return;

    setLoading(true);
    try {
      await patientService.delete(pendingDeleteId);
      await fetchPatients(pagination.currentPage, searchTerm);
    } catch (err) {
      console.error('Error deleting patient:', err);
      setError('Error deleting patient');
    } finally {
      setLoading(false);
      setPendingDeleteId(null);
    }
  }, [fetchPatients, pagination.currentPage, pendingDeleteId, searchTerm]);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchPatients(page, searchTerm);
    }
  }, [fetchPatients, pagination.totalPages, searchTerm]);

  return {
    patients,
    loading,
    error,
    pagination,
    searchTerm,
    pendingDeleteId,
    fetchPatients,
    searchPatients,
    deletePatient,
    cancelDelete,
    confirmDelete,
    goToPage,
    setPerPage: (perPage: number) => {
      setPagination(prev => ({ ...prev, perPage }));
      fetchPatients(1, searchTerm);
    },
  };
};
